import * as React from 'react';
import { omit } from 'ramda';
import { useForkRef } from '@cian/react-utils';

import { scrollingContext } from './context';
import { EScrollingBlocks } from '../../constants/scrollingBlocks';
import { IScrollingBlock } from './types';

interface IWatchRenderContainerProps {
  scrollingBlockRef: React.RefObject<HTMLDivElement | null>;
  blockId: EScrollingBlocks;
  children: React.ReactElement;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Компонент оборачивает children, добавляя слежение за mount/unmount
 * При mount в стейт refBlocksState добавится запись о том, что на странице отрендерился блок с определенным blockId
 *   * При этом, запись будет содержать ref - ссылку на DOM элемент. Через нее можно обращаться к DOM элементу
 * При unmount из стейта refBlocksState будет удалена запись о блоке с blockId
 * Идея этого компонента в том, что если обернуть им нужный view-компонент какого-либо блока на странице (например, "Похожие офферы"),
 * то можно будет понимать, когда компонент отрендерил свой view, а когда наоборот сработал unmount
 * */
export function WatchRenderContainer(props: IWatchRenderContainerProps): React.JSX.Element {
  const { blockId, scrollingBlockRef, ref } = props;
  const { setRefBlocksState } = React.useContext(scrollingContext);
  const refFork = useForkRef(scrollingBlockRef, ref);

  React.useEffect(() => {
    /** On mount */
    setRefBlocksState(prevState => ({
      ...prevState,
      [blockId]: {
        id: blockId,
        ref: scrollingBlockRef,
      } as IScrollingBlock,
    }));

    /** On unmount */
    return () => {
      setRefBlocksState(omit([blockId]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId]);

  return React.cloneElement(props.children as React.ReactElement<{ ref?: React.Ref<HTMLDivElement> }>, { ref: refFork });
}
